import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';
import {
  getLatestBriefingConditional,
  getBriefingByDateConditional,
  generateBriefing,
  saveItem,
} from '../services/dailyBriefing';
import { useToast } from '../hooks/useToast';
import { useLocalFirstQuery } from '../hooks/useLocalFirstQuery';
import { useDigestHighlights } from '../hooks/useDigestHighlights';
import useLocalMutation from '../hooks/useLocalMutation';
import useOverlayedMutations from '../hooks/useOverlayedMutations';
import { useOnline } from '../hooks/useOnline';
import { useBriefingDatesIndex } from '../hooks/useBriefingDatesIndex';
import { overlayDaily } from '../db/overlay';
import { BRIEFING_TTL_MS } from '../db/freshness';
import { enforceArchiveRetention, LIVE_BRIEFING_KEY } from '../db/retention';
import { setTitle } from '../utils/environment';
import { formatRelativeDate, getLocalIsoDate } from '../utils/dateUtils';
import { renderDigestMarkdown } from '../utils/markdown';
import { ArticleLink } from '../components/SmartLink';
import BriefingDateNav, { formatArchiveDate } from '../components/BriefingDateNav';
import BriefingDatePicker from '../components/BriefingDatePicker';
import {
  ArchiveMissingCard,
  ArchiveNotDownloadedCard,
  NoBriefingYetCard,
} from '../components/BriefingEmptyStates';

const FOLD_STATE_KEY = 'daily-briefing-fold-state';

const SOURCE_BADGES = {
  hackernews: { label: 'HN', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  github: { label: 'GitHub', className: 'bg-theme-border-subtle text-body-text' },
  websearch: { label: 'Web', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  rss: { label: 'RSS', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  wechat_feed: { label: 'WeChat', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  manual: null,
};

const SourceBadge = ({ sourceType, sourceLabel }) => {
  const badge = SOURCE_BADGES[sourceType];
  if (badge === null) return null;
  if (!badge && !sourceLabel) return null;
  const resolved = badge || { label: sourceType, className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${resolved.className}`}>
      {sourceLabel || resolved.label}
    </span>
  );
};

const SaveButton = ({ saveState, onClick, title }) => (
  <button
    onClick={onClick}
    disabled={saveState === true || saveState === 'done'}
    className={`flex-shrink-0 p-2 rounded-clay border-2 transition-all ${
      saveState === 'done'
        ? 'border-fun-mint bg-emerald-50 dark:bg-emerald-950/30 text-fun-mint'
        : 'border-theme-border-subtle text-tertiary-text hover:text-teal-600 hover:border-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/30'
    } disabled:opacity-50 disabled:cursor-not-allowed`}
    title={saveState === 'done' ? 'Saved' : (title || 'Save to reading list')}
  >
    {saveState === true ? (
      <div className="spinner-fun-sm w-4 h-4"></div>
    ) : saveState === 'done' ? (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
      </svg>
    )}
  </button>
);

const deriveMarkedAsRead = (data) => {
  const result = {};
  (data.themes || []).forEach((theme, idx) => {
    const allItems = [
      ...(theme.whats_new || []),
      ...(theme.worth_saving || []),
    ].filter(item => item.source_article_id);
    if (allItems.length > 0 && allItems.every(item => item.is_read)) {
      result[idx] = true;
    }
  });
  return result;
};

// Reason: read themes sink to the bottom so a reopened briefing leads with what
// is left to read. Order is derived from the *fetched* payload only — never the
// optimistic overlay or local mark-all-read state — so a theme never jumps out
// from under the pointer the moment it is marked read; it settles on the next
// load. Returns display order as original theme indices, because every piece of
// per-theme state (fold, saving, marking) is keyed by the payload's own index.
export const orderThemes = (themes) => {
  const list = Array.isArray(themes) ? themes : [];
  const read = deriveMarkedAsRead({ themes: list });
  const unread = [];
  const done = [];
  list.forEach((_, idx) => (read[idx] ? done : unread).push(idx));
  return [...unread, ...done];
};

const persistFoldState = (briefingIdentity, expandedThemes) => {
  try {
    localStorage.setItem(FOLD_STATE_KEY, JSON.stringify({ briefingIdentity, expandedThemes }));
  } catch {
    return;
  }
};

const getInitialFoldState = (briefingIdentity, themes) => {
  const expandedThemes = {};
  themes.forEach((_, idx) => { expandedThemes[idx] = true; });

  try {
    const saved = JSON.parse(localStorage.getItem(FOLD_STATE_KEY));
    if (
      saved?.briefingIdentity === briefingIdentity
      && saved.expandedThemes
      && typeof saved.expandedThemes === 'object'
    ) {
      return { ...expandedThemes, ...saved.expandedThemes };
    }
  } catch {
    return expandedThemes;
  }

  return expandedThemes;
};

const BriefingView = ({ archiveDate, currentDate, availableDates, datesError }) => {
  const isArchive = !!archiveDate;
  const online = useOnline();
  const { showSuccess, showError } = useToast();
  const articleNavContext = { source: 'daily-briefing' };
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [expandedThemes, setExpandedThemes] = useState({});
  const [savingItems, setSavingItems] = useState({});
  const [highlightingItem, setHighlightingItem] = useState(null);
  const [markingInProgress, setMarkingInProgress] = useState({});
  const [localReadState, setLocalReadState] = useState({ briefingIdentity: null, themes: {} });
  const { mutate } = useLocalMutation();
  const { mutations: pendingReads } = useOverlayedMutations({
    types: ['mark_read', 'mark_unread', 'mark_all_read'],
  });

  // Reason: an in-flight save belongs to the date it was started on. Bumping a
  // generation on every date change (the useLocalFirstQuery pattern) lets the
  // completion handler detect that it landed on a different briefing, where the
  // same theme/item slot is a different item entirely.
  const saveGenerationRef = useRef(0);

  const fetchFn = useCallback(async ({ etag }) => {
    return archiveDate
      ? getBriefingByDateConditional(archiveDate, { etag })
      : getLatestBriefingConditional({ etag });
  }, [archiveDate]);

  const toRecord = useCallback((payload, meta) => ({
    date: meta.key,
    payload,
    etag: meta.etag,
    fetched_at: meta.fetched_at,
    content_hash: meta.content_hash,
  }), []);

  const { data: briefing, loading, error: fetchError, refresh } = useLocalFirstQuery({
    table: 'daily_briefings',
    key: archiveDate ?? LIVE_BRIEFING_KEY,
    ttl: BRIEFING_TTL_MS,
    fetch: fetchFn,
    toRecord,
  });

  const briefingView = useMemo(
    () => overlayDaily(briefing, pendingReads),
    [briefing, pendingReads],
  );
  const briefingIdentity = briefing
    ? `${briefing.id}:${briefing.generated_at ?? briefing.briefing_date}`
    : null;

  // Reason: depends on the fetched payload alone (see orderThemes). BRIEFING_TTL_MS
  // is 0, so every mount revalidates and the order re-settles on reload.
  const themeOrder = useMemo(() => orderThemes(briefing?.themes), [briefing]);

  // Derive per-theme read state from the overlaid briefing so pending mark_read
  // mutations are reflected immediately without waiting for a server round-trip.
  const markedAsRead = useMemo(
    () => ({
      ...(briefingView ? deriveMarkedAsRead(briefingView) : {}),
      ...(localReadState.briefingIdentity === briefingIdentity ? localReadState.themes : {}),
    }),
    [briefingView, briefingIdentity, localReadState],
  );

  // Reason: offline, "we have no payload" has two very different causes and only
  // the catalogue can tell them apart. A row cached with a null payload means the
  // server answered "no briefing that day" — unless the index lists the date, in
  // which case a briefing does exist and the null row is a stale non-answer, i.e.
  // still not downloaded. A missing row plus a failed fetch is never an answer at
  // all. Both render as *not downloaded*, never as an error (§3.6).
  const isListedDate = (availableDates || []).some(d => d.briefing_date === archiveDate);
  const notDownloaded = isArchive
    && !loading
    && !briefing
    && !online
    && (!!fetchError || isListedDate);

  const error = genError || (notDownloaded ? null : fetchError?.message) || datesError || null;

  // Reason: archive rows accumulate with browsing, so trim after every settled
  // archive load (§3.6). The displayed date is passed as the protected key: on a
  // cache HIT loading clears before revalidation refreshes fetched_at, so the row
  // on screen can still be the oldest by that index and would otherwise be the
  // one evicted out from under the reader.
  useEffect(() => {
    if (!isArchive || loading) return;
    enforceArchiveRetention(undefined, archiveDate).catch(() => {});
  }, [isArchive, archiveDate, loading]);

  // Local-first digest-highlight read surface (design §5C): cache-first render,
  // network as background hydrator, queued creates overlaid as neg-id rows.
  const { isDigestItemHighlighted } = useDigestHighlights({ scope: 'all' });

  const getThemeArticleIds = (theme) => {
    const ids = new Set();
    (theme.whats_new || []).forEach(item => {
      if (item.source_article_id) ids.add(item.source_article_id);
    });
    (theme.worth_saving || []).forEach(item => {
      if (item.source_article_id) ids.add(item.source_article_id);
    });
    return [...ids];
  };

  const handleMarkAllRead = async (e, themeIdx) => {
    e.stopPropagation();
    if (markedAsRead[themeIdx] || markingInProgress[themeIdx]) return;

    const theme = (briefingView ?? briefing).themes[themeIdx];
    const ids = getThemeArticleIds(theme);
    if (ids.length === 0) return;

    setMarkingInProgress(prev => ({ ...prev, [themeIdx]: true }));
    try {
      // Reason: daily briefing has no topic_id; backend mark_all_read does a
      // topic DB lookup that would fail for a sentinel. Emit one mark_read per
      // article instead — still goes through mutation_log for offline support.
      await Promise.all(ids.map(id => mutate({ type: 'mark_read', payload: { article_id: id } })));
      setLocalReadState(prev => ({
        briefingIdentity,
        themes: {
          ...(prev.briefingIdentity === briefingIdentity ? prev.themes : {}),
          [themeIdx]: true,
        },
      }));
    } catch (err) {
      console.error('Mark-all-read failed:', err);
    } finally {
      setMarkingInProgress(prev => ({ ...prev, [themeIdx]: false }));
    }
  };

  const handleHighlightItem = async (e, text, section, articleId = null) => {
    e.stopPropagation();
    const itemKey = `briefing-${section}-${text}`;
    if (highlightingItem === itemKey) return;

    setHighlightingItem(itemKey);
    try {
      await mutate({
        type: 'digest_highlight_create',
        payload: {
          article_id: articleId,
          topic_name: `daily-briefing-${briefing?.briefing_date}`,
          digest_section: section,
          digest_text: text,
          digest_version: briefing?.generated_at,
        },
      });
      // No read-back needed: the optimistic neg-id row surfaces through
      // useDigestHighlights, and the post-drain refresh swaps in the server row.
    } catch (err) {
      console.error('Failed to highlight item:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setHighlightingItem(null);
    }
  };

  useEffect(() => {
    if (!briefingIdentity) return;
    const expanded = getInitialFoldState(briefingIdentity, briefing.themes || []);
    setExpandedThemes(expanded);
    persistFoldState(briefingIdentity, expanded);
  }, [briefingIdentity, briefing?.themes?.length]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setGenError(null);
      await generateBriefing({ force: !!briefing });
      await refresh();
      showSuccess('Briefing generated!');
    } catch (err) {
      setGenError(err.message);
      showError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const toggleTheme = (idx) => {
    setExpandedThemes(prev => {
      const expanded = { ...prev, [idx]: !prev[idx] };
      persistFoldState(briefingIdentity, expanded);
      return expanded;
    });
  };

  const handleSaveItem = async (themeIndex, section, itemIndex) => {
    const key = `${themeIndex}-${section}-${itemIndex}`;
    if (savingItems[key]) return;
    const gen = saveGenerationRef.current;

    setSavingItems(prev => ({ ...prev, [key]: true }));
    try {
      const result = await saveItem(briefing.id, themeIndex, section, itemIndex);
      const action = result.action === 'promoted' ? 'Added to reading list' : 'Saved and processing';
      // Reason: the save really happened, so the toast still fires — but the
      // per-slot button state must not be written onto a briefing this save was
      // never about (see saveGenerationRef).
      showSuccess(action);
      if (gen !== saveGenerationRef.current) return;
      setSavingItems(prev => ({ ...prev, [key]: 'done' }));
    } catch (err) {
      showError(err.message);
      if (gen !== saveGenerationRef.current) return;
      setSavingItems(prev => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    setTitle(archiveDate
      ? `Daily Briefing ${archiveDate} · Read It Never`
      : 'Daily Briefing · Read It Never');
  }, [archiveDate]);

  // Reason: per-item save/generate feedback is keyed by theme index, which is
  // only meaningful within one briefing — clear it when the date changes, and
  // invalidate any save still in flight for the previous date.
  useEffect(() => {
    saveGenerationRef.current += 1;
    setSavingItems({});
    setGenError(null);
  }, [archiveDate]);

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Reason: PageHeader's actions slot wraps the controls onto their own
          right-aligned row, so the worst case (archive: date badge + two arrows
          + picker) does not push a phone viewport into a horizontal scroll that
          hides the picker off-screen. */}
      <PageHeader
        emoji="☀️"
        title="Daily Briefing"
        subtitle={isArchive
          ? `Archived · ${formatArchiveDate(archiveDate)}${briefing ? ` · ${briefing.article_count || briefing.themes?.length || 0} sources` : ''}`
          : briefing
            ? `${briefing.article_count || briefing.themes?.length || 0} sources · Updated ${formatRelativeDate(briefing.generated_at || briefing.briefing_date)}`
            : 'Your curated news across the web'}
        backTo="/dashboard"
        backLabel="Back to dashboard"
        actions={
          <>
            <BriefingDateNav
              dates={availableDates}
              anchorDate={archiveDate ?? briefing?.briefing_date ?? currentDate}
              isArchive={isArchive}
            />
            <BriefingDatePicker
              dates={availableDates}
              displayedDate={archiveDate ?? briefing?.briefing_date ?? currentDate}
            />
            {!isArchive && (
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="btn-clay text-sm font-medium"
              >
                {generating ? (
                  <>
                    <div className="spinner-fun-sm w-4 h-4 mr-2 inline-block"></div>
                    Generating...
                  </>
                ) : (
                  'Generate New'
                )}
              </button>
            )}
          </>
        }
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="notice-clay-error flex items-center gap-2 mb-6">
            <span className="text-xl" role="img" aria-hidden="true">😅</span>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="spinner-fun-lg mx-auto mb-4"></div>
              <p className="font-display text-lg text-secondary-text">Loading briefing...</p>
            </div>
          </div>
        ) : notDownloaded ? (
          <ArchiveNotDownloadedCard archiveDate={archiveDate} />
        ) : !briefing && isArchive ? (
          <ArchiveMissingCard archiveDate={archiveDate} />
        ) : !briefing ? (
          <NoBriefingYetCard generating={generating} onGenerate={handleGenerate} />
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            {briefing.summary && (
              <div className="card-clay-static p-4 sm:p-6 border-l-4 border-l-teal-400">
                <p className="text-body-text leading-relaxed">{briefing.summary}</p>
              </div>
            )}

            {/* Theme Cards */}
            {themeOrder.map((themeIdx, position) => {
              const theme = (briefingView ?? briefing).themes[themeIdx];
              // Reason: keyed by payload index, not name — theme names are
              // LLM-generated and can repeat within a briefing. Now that cards
              // reorder, React moves keyed nodes, and a duplicate key would carry
              // fold/scroll state onto the wrong card. The index is the stable
              // identity here: all per-theme state is keyed by it.
              return (
              <div
                key={themeIdx}
                className="card-clay-static !p-0 overflow-hidden"
                style={{ animationDelay: `${position * 100}ms` }}
              >
                {/* Theme Header */}
                <div
                  className="px-2 py-3 sm:p-4 border-b-2 border-clay-border cursor-pointer hover:bg-surface-hover transition-colors"
                  onClick={() => toggleTheme(themeIdx)}
                >
                  <div className="flex items-center">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className={`text-lg text-tertiary-text transition-transform duration-200 flex-shrink-0 ${
                        expandedThemes[themeIdx] ? 'rotate-0' : '-rotate-90'
                      }`}>
                        ▼
                      </span>
                      <h2 className="font-display text-lg font-semibold text-heading min-w-0 break-words">
                        {theme.name}
                      </h2>
                      <span className="badge-fun-sky text-xs flex-shrink-0">
                        {theme.whats_new?.length || 0} insights
                      </span>
                    </div>
                  </div>
                </div>

                {/* Theme Content — 3 sections */}
                {expandedThemes[themeIdx] && (
                  <div className="px-2 py-4 sm:p-6 space-y-6">

                    {/* What's New */}
                    {theme.whats_new && theme.whats_new.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">📰</span>
                          What&apos;s New
                        </h3>
                        <ul className="space-y-2">
                          {theme.whats_new.map((item, itemIdx) => {
                            const isHighlighted = isDigestItemHighlighted(item.text);
                            const itemKey = `briefing-whats_new-${item.text}`;
                            const isHighlighting = highlightingItem === itemKey;
                            return (
                              <li
                                key={itemIdx}
                                className={`group text-sm text-body-text leading-relaxed pl-2 sm:pl-4 border-l-2 ${
                                  isHighlighted ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 py-2 pr-2 rounded-r' : 'border-blue-300'
                                }`}
                              >
                                <div className="flex items-start justify-between w-full">
                                  <div className="flex-1">
                                    {renderDigestMarkdown(item.text, item.source_article_id, articleNavContext)}
                                    <SourceBadge sourceType={item.source_type} sourceLabel={item.source_label} />
                                  </div>
                                  <button
                                    onClick={(e) => handleHighlightItem(e, item.text, 'whats_new', item.source_article_id)}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 cursor-default'
                                        : 'text-tertiary-text hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Analysis */}
                    {theme.analysis && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">🔍</span>
                          Analysis
                        </h3>
                        <div className="text-secondary-text leading-relaxed border-l-3 border-teal-300 pl-4 space-y-3">
                          {theme.analysis.split('\n\n').map((paragraph, pIdx) => (
                            <p key={pIdx}>{renderDigestMarkdown(paragraph)}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Worth Saving */}
                    {theme.worth_saving && theme.worth_saving.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">⭐</span>
                          Worth Saving
                        </h3>
                        <div className="space-y-2">
                          {theme.worth_saving.map((item, itemIdx) => {
                            const saveKey = `${themeIdx}-worth_saving-${itemIdx}`;
                            const saveState = savingItems[saveKey];
                            return (
                              <div
                                key={itemIdx}
                                className="group p-3 rounded-clay border-2 border-clay-border hover:border-teal-300 hover:shadow-clay-sm transition-all"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      {item.source_article_id ? (
                                        <ArticleLink
                                          articleId={item.source_article_id}
                                          context={articleNavContext}
                                          onClick={(e) => e.stopPropagation()}
                                          className="font-medium text-heading hover:text-primary-600 transition-colors text-left no-underline"
                                        >
                                          {item.title}
                                        </ArticleLink>
                                      ) : (
                                        <a
                                          href={item.source_url?.startsWith('http') ? item.source_url : '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="font-medium text-heading hover:text-primary-600 transition-colors"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {item.title}
                                        </a>
                                      )}
                                      <SourceBadge sourceType={item.source_type} sourceLabel={item.source_label} />
                                    </div>
                                    <p className="text-secondary-text leading-relaxed italic">
                                      {renderDigestMarkdown(item.reason)}
                                    </p>
                                  </div>
                                  <SaveButton
                                    saveState={saveState}
                                    onClick={() => handleSaveItem(themeIdx, 'worth_saving', itemIdx)}
                                    title="Save article to reading list"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {!isArchive && (
                      <div className="pt-4 border-t-2 border-clay-border flex items-center justify-end">
                        <button
                          onClick={(e) => handleMarkAllRead(e, themeIdx)}
                          disabled={markedAsRead[themeIdx] || markingInProgress[themeIdx] || getThemeArticleIds(theme).length === 0}
                          className={`btn-clay text-sm ${
                            markedAsRead[themeIdx]
                              ? 'bg-emerald-100 dark:bg-emerald-900/40 border-fun-mint text-emerald-700 dark:text-emerald-300'
                              : 'bg-fun-mint border-emerald-400 text-white hover:bg-emerald-500'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          title={markedAsRead[themeIdx] ? 'All read' : 'Mark all as read'}
                        >
                          {markingInProgress[themeIdx]
                            ? 'Marking...'
                            : markedAsRead[themeIdx]
                              ? '✨ All Read'
                              : '✓ Mark All Read'}
                        </button>
                      </div>
                    )}

                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const DailyBriefing = () => {
  const { date: dateParam } = useParams();
  const { index: datesIndex, loading: datesLoading, error: datesError } = useBriefingDatesIndex();

  // Reason: the server is the sole authority on which date is current — the
  // browser's clock disagrees with it for hours a day in any offset zone, in
  // both directions. The dated route therefore must not render (and must not let
  // useLocalFirstQuery write a cache row) before the index resolves. The local
  // date is a last-resort fallback for when the index is unreachable; the
  // backend's own 404 for non-past dates is what actually keeps mutable content
  // out of archive cache keys (§3.6).
  if (dateParam && datesLoading) {
    return <PageLoading message="Loading briefing..." />;
  }

  // Reason: only the server's date may block an archive route. Falling back to
  // the browser clock here redirects a genuinely archived date away from its own
  // page for any user west of UTC after their local 17:00, and a completed
  // redirect cannot be undone once the index revalidates. With the date unknown
  // the request goes through and the backend's own `>= current_date` 404 is what
  // keeps mutable content out of an archive cache key (§3.6) — the worst case is
  // a null payload rendered as "not downloaded".
  const serverCurrentDate = datesIndex?.current_date ?? null;
  if (dateParam && serverCurrentDate && dateParam >= serverCurrentDate) {
    return <Navigate to="/daily" replace />;
  }

  // The local date is still the anchor the date arrows fall back to when no
  // briefing and no index are available; it just no longer gates the route.
  const currentDate = serverCurrentDate ?? getLocalIsoDate();

  return (
    <BriefingView
      archiveDate={dateParam || null}
      currentDate={currentDate}
      availableDates={datesIndex?.dates ?? []}
      datesError={datesError}
    />
  );
};

export default DailyBriefing;
